import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // Store full product objects for display on Favorites page
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = localStorage.getItem('favorites');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Clear favorites when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setFavorites([]);
      localStorage.removeItem('favorites');
    }
  }, [isAuthenticated]);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('favorites', JSON.stringify(favorites));
    } catch (e) {
      console.error('Failed to save favorites:', e);
    }
  }, [favorites]);

  // Get product ID from product object or direct ID
  const getProductId = (product) => {
    if (typeof product === 'object' && product !== null) {
      return product.id || product._id;
    }
    return product;
  };

  const addToFavorites = useCallback((product) => {
    const productId = getProductId(product);
    setFavorites((prev) => {
      // Check if already exists
      if (prev.some((p) => getProductId(p) === productId)) return prev;
      // Store full product object if provided
      if (typeof product === 'object' && product !== null) {
        return [...prev, product];
      }
      return [...prev, { id: productId }];
    });
  }, []);

  const removeFromFavorites = useCallback((product) => {
    const productId = getProductId(product);
    setFavorites((prev) => prev.filter((p) => getProductId(p) !== productId));
  }, []);

  const toggleFavorite = useCallback((product) => {
    const productId = getProductId(product);
    setFavorites((prev) => {
      const exists = prev.some((p) => getProductId(p) === productId);
      if (exists) {
        return prev.filter((p) => getProductId(p) !== productId);
      }
      // Store full product object if provided
      if (typeof product === 'object' && product !== null) {
        return [...prev, product];
      }
      return [...prev, { id: productId }];
    });
  }, []);

  const isFavorite = useCallback(
    (product) => {
      const productId = getProductId(product);
      return favorites.some((p) => getProductId(p) === productId);
    },
    [favorites]
  );

  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  // MEMOIZED value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      favorites,
      favoritesCount: favorites.length,
      addToFavorites,
      removeFromFavorites,
      toggleFavorite,
      isFavorite,
      clearFavorites,
    }),
    [
      favorites,
      addToFavorites,
      removeFromFavorites,
      toggleFavorite,
      isFavorite,
      clearFavorites,
    ]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesContext;
